import { useState } from 'react';
import { ShoppingBag, Plus, X, Check, Trash2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminMerch({ merch, onRefresh }) {
  const [editingMerch, setEditingMerch] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newMerch, setNewMerch] = useState({
    name: '', description: '', price: 29.99, image_url: '', category: 'apparel', sizes: ['S', 'M', 'L', 'XL']
  });

  const createMerch = async () => {
    if (!newMerch.name) {
      toast.error('Name is required');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/merch`, newMerch);
      toast.success('Merch created!');
      setShowCreate(false);
      setNewMerch({ name: '', description: '', price: 29.99, image_url: '', category: 'apparel', sizes: ['S', 'M', 'L', 'XL'] });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create merch');
    }
  };

  const saveMerch = async (item) => {
    try {
      await axios.put(`${API_URL}/admin/merch/${item.id}`, {
        name: item.name,
        description: item.description,
        price: item.price,
        image_url: item.image_url,
        category: item.category
      });
      toast.success('Merch updated');
      setEditingMerch(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update merch');
    }
  };

  const deleteMerch = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API_URL}/admin/merch/${id}`);
      toast.success('Merch deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete merch');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Manage Merchandise</h2>
        <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="create-merch-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Item
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="glass-card p-6 mb-4">
          <h3 className="text-lg font-semibold mb-4">Create New Merch</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Name</Label>
              <Input value={newMerch.name} onChange={(e) => setNewMerch({...newMerch, name: e.target.value})} className="input-dark mt-1" />
            </div>
            <div>
              <Label>Price ($)</Label>
              <Input type="number" step="0.01" value={newMerch.price} onChange={(e) => setNewMerch({...newMerch, price: parseFloat(e.target.value)})} className="input-dark mt-1" />
            </div>
            <div>
              <Label>Category</Label>
              <select value={newMerch.category} onChange={(e) => setNewMerch({...newMerch, category: e.target.value})} className="input-dark mt-1 w-full rounded-md">
                <option value="apparel">Apparel</option>
                <option value="accessories">Accessories</option>
                <option value="vinyl">Vinyl</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label>Image URL</Label>
              <Input value={newMerch.image_url} onChange={(e) => setNewMerch({...newMerch, image_url: e.target.value})} className="input-dark mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Input value={newMerch.description} onChange={(e) => setNewMerch({...newMerch, description: e.target.value})} className="input-dark mt-1" />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button>
            <button onClick={createMerch} className="btn-primary"><Plus className="w-4 h-4 mr-2" /> Create</button>
          </div>
        </div>
      )}

      {/* Merch List */}
      {merch.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <ShoppingBag className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Merchandise Yet</h3>
          <p className="text-gray-400 mb-4">Add your first merch item</p>
        </div>
      ) : (
        merch.map(item => (
          <div key={item.id} className="glass-card p-4" data-testid={`merch-item-${item.id}`}>
            {editingMerch?.id === item.id ? (
              <div className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div><Label>Name</Label><Input value={editingMerch.name} onChange={(e) => setEditingMerch({...editingMerch, name: e.target.value})} className="input-dark mt-1" /></div>
                  <div><Label>Price</Label><Input type="number" step="0.01" value={editingMerch.price} onChange={(e) => setEditingMerch({...editingMerch, price: parseFloat(e.target.value)})} className="input-dark mt-1" /></div>
                  <div><Label>Image URL</Label><Input value={editingMerch.image_url} onChange={(e) => setEditingMerch({...editingMerch, image_url: e.target.value})} className="input-dark mt-1" /></div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditingMerch(null)} className="btn-ghost"><X className="w-4 h-4" /></button>
                  <button onClick={() => saveMerch(editingMerch)} className="btn-primary"><Check className="w-4 h-4" /></button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <img src={item.image_url || '/placeholder-merch.png'} alt={item.name} className="w-16 h-16 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{item.name}</h3>
                  <p className="text-sm text-gray-400">{item.category}</p>
                </div>
                <div className="text-right mr-4">
                  <p className="font-bold">${item.price?.toFixed(2)}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditingMerch({...item})} className="btn-ghost">Edit</button>
                  <button onClick={() => deleteMerch(item.id)} className="btn-ghost text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
